import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { TipoDocumentoItemVendaEnum } from '@shared/enums/vendas/tipo-documento-item-venda.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { isStatusEditavelNFeSaida } from '@shared/helpers/nota-fiscal-saida-acoes.helper';
import { compararValoresOrdenacao } from '@shared/helpers/ordenacao-tabela.helper';
import { StatusNFeSaidaEnum } from '@shared/enums/repo-dfe/status-nfe-saida.enum';
import { classeBadgeStatusNFeSaida, iconeStatusNFeSaida } from '@shared/helpers/nota-fiscal-saida-status.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalSaidaListaService } from '@shared/services/apis/repo-dfe/nota-fiscal-saida-lista.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { VendaDocumentoService } from '@shared/services/apis/vendas/venda-documento.service';
import { VendaService } from '@shared/services/apis/vendas/venda.service';
import { KanbanCor } from '@shared/services/models/vendas/kanban-cor.model';
import { ItemVendidoClienteStruct } from '@shared/services/structs/orquestrador-vendas/item-vendido-cliente.struct';
import { PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';

export type UltimasVendasDialogData = {
  idPessoa: number;
  nomePessoa: string;
};

type AbaUltimasVendas = 'notas' | 'vendas' | 'produtos';
type ColunaOrdenacao = 'numero' | 'data' | 'total' | 'situacao' | 'descricao' | 'valorUnitario' | 'quantidade' | 'tipo';
type DirecaoOrdenacao = 'asc' | 'desc';
type ValorOrdenacao = string | number | null;

/** As linhas guardam o valor de cada coluna já pronto para exibir e ordenar. */
type NotaFiscalLinha = {
  idNFeSaida: number;
  idStatusNFeSaida: number;
  numero: string;
  data: string;
  total: number;
  situacao: string;
};

type VendaLinha = {
  idVenda: number;
  idKanbanCor: number;
  numero: number;
  data: string;
  total: number;
  situacao: string;
};

type ProdutoLinha = ItemVendidoClienteStruct & { tipo: string };

/**
 * Consulta somente leitura das últimas vendas da pessoa (CO-01 a CO-09): uma aba por rotina, cada uma com
 * a permissão da sua rotina (PA-06, PA-07), carregada na primeira vez que é aberta e com a ordenação própria.
 */
@Component({
  selector: 'app-ultimas-vendas-dialog',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet, FormsModule, ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatSortModule, MatTableModule, MatTabsModule, MatTooltipModule, AbntPipe, EmptyStateComponent,
    LoadingBoxDefaultListComponent, TabsScrollDirective],
  templateUrl: './ultimas-vendas-dialog.component.html',
  styleUrls: ['./ultimas-vendas-dialog.component.css'],
})
export class UltimasVendasDialogComponent implements OnInit {
  public readonly abas: AbaUltimasVendas[] = ['notas', 'vendas', 'produtos'];
  public readonly colunasNotas = ['numero', 'data', 'total', 'situacao'];
  public readonly colunasVendas = ['numero', 'data', 'total', 'situacao'];
  public readonly colunasProdutos = ['descricao', 'valorUnitario', 'quantidade', 'data', 'tipo', 'numero'];

  public possuiPermissaoNotas = false;
  public possuiPermissaoVendas = false;
  public notas: NotaFiscalLinha[] = [];
  public vendas: VendaLinha[] = [];
  public produtos: ProdutoLinha[] = [];
  /** As linhas na ordem da aba, recalculadas só quando os dados carregam ou a ordenação muda. */
  public notasExibidas: NotaFiscalLinha[] = [];
  public vendasExibidas: VendaLinha[] = [];
  public produtosExibidos: ProdutoLinha[] = [];
  public isCarregando: Record<AbaUltimasVendas, boolean> = { notas: true, vendas: true, produtos: true };
  /** CO-04: padrão Data decrescente, guardada por aba enquanto o dialog está aberto. */
  public ordenacao: Record<AbaUltimasVendas, { coluna: ColunaOrdenacao; direcao: DirecaoOrdenacao }> = {
    notas: { coluna: 'data', direcao: 'desc' },
    vendas: { coluna: 'data', direcao: 'desc' },
    produtos: { coluna: 'data', direcao: 'desc' },
  };
  public pesquisaProdutos = new FormControl('', { nonNullable: true });
  /** A pesquisa da última consulta, que decide o estado vazio (CO-07), e não o que está digitado. */
  public pesquisaAplicada = '';

  private readonly abasAbertas = new Set<AbaUltimasVendas>();
  private coresKanban: KanbanCor[] = [];

  constructor(
    private dialogRef: MatDialogRef<UltimasVendasDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UltimasVendasDialogData,
    private router: Router,
    private authService: AuthService,
    private notaFiscalSaidaListaService: NotaFiscalSaidaListaService,
    private vendaDocumentoService: VendaDocumentoService,
    private vendaService: VendaService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.possuiPermissaoNotas = permissoes.includes(PermissaoEnum.Vendas_NotasFiscais_Visualizar);
    this.possuiPermissaoVendas = permissoes.includes(PermissaoEnum.Vendas_ProcessoVenda_Visualizar);
    this.abrirAba(0);
  }

  /** Cada aba consulta a API na primeira vez que é aberta; sem permissão, não consulta (PA-06). */
  public abrirAba(indice: number): void {
    const aba = this.abas[indice];
    if (!aba || this.abasAbertas.has(aba)) return;
    this.abasAbertas.add(aba);

    switch (aba) {
      case 'notas':
        if (this.possuiPermissaoNotas) this.carregarNotas();
        return;
      case 'vendas':
        if (this.possuiPermissaoVendas) this.carregarVendas();
        return;
      case 'produtos':
        if (this.possuiPermissaoVendas) this.carregarProdutos();
        return;
    }
  }

  /** CO-03: aplica com Enter ou na lupa, nunca enquanto digita. */
  public pesquisarProdutos(): void {
    if (this.isCarregando.produtos) return;
    this.pesquisaAplicada = this.pesquisaProdutos.value.trim();
    this.carregarProdutos();
  }

  /** Cada aba guarda a sua ordenação; sem direção (terceiro clique), volta ao padrão Data decrescente. */
  public atualizarOrdenacao(aba: AbaUltimasVendas, ordenacao: Sort): void {
    this.ordenacao[aba] = ordenacao.direction
      ? { coluna: ordenacao.active as ColunaOrdenacao, direcao: ordenacao.direction }
      : { coluna: 'data', direcao: 'desc' };
    this.aplicarOrdenacao(aba);
  }

  public classeBadgeNota(nota: NotaFiscalLinha): string {
    return classeBadgeStatusNFeSaida(nota.idStatusNFeSaida);
  }

  public iconeNota(nota: NotaFiscalLinha): string {
    return iconeStatusNFeSaida(nota.idStatusNFeSaida);
  }

  /** A cor da etapa pelo mesmo resolvedor do board, no tema de agora. */
  public corDaEtapa(venda: VendaLinha): PvCorVm {
    return resolverCor(venda.idKanbanCor, this.coresKanban, document.body.classList.contains('dark-mode'));
  }

  /**
   * CO-05: status editável abre o documento; os demais, a visualização (mesma regra da lista de NF-e). A nota Em
   * transmissão abre no documento, que leva sozinho à visualização quando ela não tem transmissão não realizada.
   */
  public abrirNotaFiscal(nota: NotaFiscalLinha): void {
    this.dialogRef.close();
    if (isStatusEditavelNFeSaida(nota.idStatusNFeSaida) || nota.idStatusNFeSaida === StatusNFeSaidaEnum.EmTransmissao) {
      this.router.navigate(['/vendas/nota-fiscal-saida/documento', nota.idNFeSaida]);
      return;
    }
    this.router.navigate(['/vendas/nota-fiscal-saida/documento', nota.idNFeSaida, 'visualizar']);
  }

  public abrirVenda(idVenda: number): void {
    this.dialogRef.close();
    this.router.navigate(['/vendas/processo-venda/documento', idVenda]);
  }

  /**
   * O item não traz o status da NF-e: a nota abre no documento, que leva sozinho à visualização quando o
   * status não é editável.
   */
  public abrirDocumentoDoProduto(produto: ProdutoLinha): void {
    if (produto.tipoDocumentoItemVenda === TipoDocumentoItemVendaEnum.NotaFiscal) {
      this.dialogRef.close();
      this.router.navigate(['/vendas/nota-fiscal-saida/documento', produto.idDocumento]);
      return;
    }
    this.abrirVenda(produto.idDocumento);
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  private carregarNotas(): void {
    this.isCarregando.notas = true;
    this.notaFiscalSaidaListaService.getUltimasDoDestinatario(this.data.idPessoa).subscribe({
      next: response => {
        this.isCarregando.notas = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.notas = (response.ultimasNotas?.notas ?? []).map(nota => ({
          idNFeSaida: nota.idNFeSaida,
          idStatusNFeSaida: nota.idStatusNFeSaida,
          // Mesmo formato da lista de NF-e de saída: número / série.
          numero: nota.numero != null ? `${nota.numero}${nota.serie != null ? ` / ${nota.serie}` : ''}` : '-',
          data: nota.dataEmissao,
          total: nota.valorTotal,
          situacao: nota.nomeStatusNFeSaida || '-',
        }));
        this.aplicarOrdenacao('notas');
      },
      error: () => {
        this.isCarregando.notas = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as notas fiscais agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private carregarVendas(): void {
    this.isCarregando.vendas = true;
    forkJoin({
      response: this.vendaService.getHistoricoDoCliente(this.data.idPessoa, VendaTipoDocumentoEnum.PedidoDeVenda),
      // As cores da etapa vêm da mesma fonte do documento de venda; sem elas, a etapa fica na cor padrão.
      configuracoes: this.vendaDocumentoService.getConfiguracoes().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ response, configuracoes }) => {
        this.isCarregando.vendas = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.coresKanban = configuracoes?.isError ? [] : (configuracoes?.kanban?.cores ?? []);
        this.vendas = (response.historico?.vendas ?? []).map(venda => ({
          idVenda: venda.idVenda,
          idKanbanCor: venda.idKanbanCor,
          numero: venda.numero,
          data: venda.dataEmissao,
          total: venda.valorTotal,
          situacao: venda.nomeEtapaKanban || 'Sem etapa',
        }));
        this.aplicarOrdenacao('vendas');
      },
      error: () => {
        this.isCarregando.vendas = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as vendas agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private carregarProdutos(): void {
    this.isCarregando.produtos = true;
    this.vendaDocumentoService.getItensDoCliente(this.data.idPessoa, this.pesquisaAplicada).subscribe({
      next: response => {
        this.isCarregando.produtos = false;
        if (response.isError) {
          this.produtos = [];
          this.aplicarOrdenacao('produtos');
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.produtos = (response.itens ?? []).map(item => ({ ...item, tipo: this.descreverTipo(item.tipoDocumentoItemVenda) }));
        this.aplicarOrdenacao('produtos');
      },
      error: () => {
        this.isCarregando.produtos = false;
        this.produtos = [];
        this.aplicarOrdenacao('produtos');
        this.alertService.show('Aviso',
          'Não foi possível carregar os produtos agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private descreverTipo(tipoDocumentoItemVenda: number): string {
    switch (tipoDocumentoItemVenda) {
      case TipoDocumentoItemVendaEnum.Orcamento: return 'Orçamento';
      case TipoDocumentoItemVendaEnum.PedidoDeVenda: return 'Venda';
      case TipoDocumentoItemVendaEnum.NotaFiscal: return 'Nota fiscal';
      default: return '-';
    }
  }

  private aplicarOrdenacao(aba: AbaUltimasVendas): void {
    switch (aba) {
      case 'notas':
        this.notasExibidas = this.ordenar(this.notas, aba);
        return;
      case 'vendas':
        this.vendasExibidas = this.ordenar(this.vendas, aba);
        return;
      case 'produtos':
        this.produtosExibidos = this.ordenar(this.produtos, aba);
        return;
    }
  }

  private ordenar<T extends Partial<Record<ColunaOrdenacao, ValorOrdenacao>>>(linhas: T[], aba: AbaUltimasVendas): T[] {
    const { coluna, direcao } = this.ordenacao[aba];
    // Os números dentro do texto ("45 / 1") ganham zeros à esquerda para ordenar pelo valor, e não dígito a dígito.
    const chave = (valor: string | number) =>
      typeof valor === 'string' ? valor.replace(/\d+/g, digitos => digitos.padStart(20, '0')) : valor;

    return [...linhas].sort((primeira, segunda) => {
      const primeiroValor = primeira[coluna] ?? null;
      const segundoValor = segunda[coluna] ?? null;

      // Sem valor fica sempre no fim, nas duas direções.
      if (primeiroValor === null || segundoValor === null) {
        return primeiroValor === segundoValor ? 0 : primeiroValor === null ? 1 : -1;
      }

      const resultado = compararValoresOrdenacao(chave(primeiroValor), chave(segundoValor));
      return direcao === 'asc' ? resultado : -resultado;
    });
  }
}
